import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { FormGroup } from '@angular/forms';
import { ApiHelperService } from 'projects/apna-lib/src/lib/services/api-helper.service';
import { ConfigService } from 'projects/apna-lib/src/lib/services/config.service';

export const admissionsFeedbackOptions = {
  FEEDBACK: 'Feedback',
  SUPPORT: 'Support',
  COMPLAINT: 'Complaint',
  OTHER: 'Other',
};

@Component({
  selector: 'app-help-feedback',
  templateUrl: './help-feedback.component.html',
  styleUrls: ['./help-feedback.component.scss']
})
export class HelpFeedbackComponent implements OnInit {
  @Input() puid!: string;
  @Input() studentName!: string;

  public dataModel: string = '';  // ✅ Changed: Empty to prevent auto-submit
  public layout: FormlyFieldConfig[] = [];
  public textFieldsLayout: FormlyFieldConfig[] = [];
  public model: any = {};
  public form: FormGroup = new FormGroup({});  // ✅ Added form group
  public options: FormlyFormOptions = {};  // ✅ Added options

  feedbackOptions = this.configService.getConfig1('foreignAssist', 'courseFeedbackOptions');

  constructor(
    private modalService: NgbModal,
    private configService: ConfigService,
    private apiHelperService: ApiHelperService
  ) {}

  ngOnInit(): void {
    this.dataPreProcessor();
  }

  openHelp(content: any): void {
    this.modalService.open(content, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });
  }

  selectEmoji(field: string, value: number): void {
    this.model[field] = String(value);
  }

  isEmojiSelected(field: string, value: number): boolean {
    return this.model[field] === String(value);
  }

  setLayout(): void {
    this.layout = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            key: 'category',
            type: 'select',
            className: 'col-12 mb-3',
            templateOptions: {
              label: 'Please select a category',
              required: true,
              options: [
                { label: 'Please select' },
                { value: admissionsFeedbackOptions.SUPPORT, label: 'Support' },
                { value: admissionsFeedbackOptions.FEEDBACK, label: 'Feedback' },
                { value: admissionsFeedbackOptions.COMPLAINT, label: 'Complaint' },
                { value: admissionsFeedbackOptions.OTHER, label: 'Other' },
              ],
            },
          },
          {
            key: 'comment',
            type: 'textarea',
            className: 'col-12 mb-3',
            templateOptions: {
              label: 'Please describe your issue or request',
              placeholder: 'Please provide detailed information about your request...',
              rows: 4,
            },
            hideExpression: (model: any) => model.category === 'Feedback',
            expressionProperties: {
              'templateOptions.required': (model: any) => model.category !== 'Feedback',
            },
          },
          {
            type: 'button',
            className: 'col-12',
            templateOptions: {
              className: 'd-grid',
              btnType: 'primary py-3 rounded rounded-1 text-light mb-2',
              type: 'button',  // ✅ Changed from 'submit' to 'button'
              text: 'Submit',
              onClick: () => this.handleSubmit(),  // ✅ Manual handler
            },
            hideExpression: (model: any) => model.category === 'Feedback',
          },
        ],
      },
    ];

    this.textFieldsLayout = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            key: 'confusingAreas',
            type: 'textarea',
            className: 'col-12 mb-3',
            templateOptions: {
              label: 'What part of the platform was confusing or needed more support?',
              placeholder: 'Please specify the areas or features that were unclear...',
              required: true,
              rows: 3,
            },
          },
          {
            key: 'missingFeatures',
            type: 'textarea',
            className: 'col-12 mb-3',
            templateOptions: {
              label: "What features or information were you expecting but didn't find?",
              placeholder: 'Please describe features or information you were looking for...',
              required: true,
              rows: 3,
            },
          },
          {
            key: 'additionalFeedback',
            type: 'textarea',
            className: 'col-12 mb-3',
            templateOptions: {
              label: 'Any other feedback',
              placeholder: 'Share any additional thoughts or suggestions...',
              required: false,
              rows: 3,
            },
          },
          {
            type: 'button',
            className: 'col-12',
            templateOptions: {
              className: 'd-grid',
              btnType: 'primary py-3 rounded rounded-1 text-light mb-2',
              type: 'button',  // ✅ Changed from 'submit' to 'button'
              text: 'Submit',
              onClick: () => this.handleSubmit(),  // ✅ Manual handler
            },
          },
        ],
      },
    ];
  }

  dataPreProcessor(): void {
    this.model = {
      category: null,
      comment: null,
      experience: null,
      courseDiscovery: null,
      applicationProcess: null,
      confusingAreas: null,
      missingFeatures: null,
      additionalFeedback: null,
    };

    this.setLayout();
  }

  // ✅ Remove this - not needed anymore
  // dataProcessor(data: any): any { ... }

  // ✅ Remove this - not needed anymore  
  // onSubmitResponse(event?: any): void { ... }

  // ✅ New unified submit handler
  handleSubmit(): void {
    console.log('🔍 Submit clicked');
    console.log('🔍 Current model:', this.model);

    // Validate based on category
    if (!this.model.category) {
      this.apiHelperService.openSnackBar('Please select a category');
      return;
    }

    if (this.model.category === 'Feedback') {
      // Validate feedback fields
      if (!this.model.experience || !this.model.courseDiscovery || !this.model.applicationProcess) {
        this.apiHelperService.openSnackBar('Please answer all emoji rating questions');
        return;
      }
      if (!this.model.confusingAreas || !this.model.missingFeatures) {
        this.apiHelperService.openSnackBar('Please fill all required feedback fields');
        return;
      }
    } else {
      // Validate non-feedback fields
      if (!this.model.comment) {
        this.apiHelperService.openSnackBar('Please describe your issue or request');
        return;
      }
    }

    this.submitFeedback();
  }

  submitFeedback(): void {
    const finalData = {
      ...this.model,
      studentName: this.studentName || null,
      partnerId: this.puid || null,
      timestamp: new Date().toISOString(),
      source: 'admissions-platform',
    };

    // Remove empty values
    const cleanedData = this.removeEmpty(finalData);

    console.log('🔍 Submitting to FA-ADMISSIONS (port 5001)...');
    console.log('🔍 Endpoint: admissions-feedbacks');
    console.log('🔍 Payload:', cleanedData);

    this.apiHelperService
      .postDataWithShortlistingUrl('admissions-feedbacks', cleanedData)
      .then((res: any) => {
        console.log('✅ Success Response:', res);
        
        this.modalService.dismissAll();
        this.model = {};
        this.dataPreProcessor();

        switch (this.model.category) {
          case admissionsFeedbackOptions.SUPPORT:
            this.apiHelperService.openSnackBar('Support Ticket Raised successfully');
            break;
          case admissionsFeedbackOptions.FEEDBACK:
            this.apiHelperService.openSnackBar('Feedback Submitted successfully');
            break;
          case admissionsFeedbackOptions.COMPLAINT:
            this.apiHelperService.openSnackBar('Complaint Raised successfully');
            break;
          case admissionsFeedbackOptions.OTHER:
            this.apiHelperService.openSnackBar('Request submitted successfully');
            break;
          default:
            this.apiHelperService.openSnackBar('Submitted successfully');
        }
      })
      .catch((error) => {
        console.error('❌ Error submitting feedback:', error);
        console.error('❌ Error details:', error.message);
        this.apiHelperService.openSnackBar('Failed to submit. Please try again.');
      });
  }

  removeEmpty(obj: any): any {
    const cleaned = { ...obj };
    Object.keys(cleaned).forEach((key) => {
      if (cleaned[key] === null || cleaned[key] === undefined || cleaned[key] === '') {
        delete cleaned[key];
      } else if (typeof cleaned[key] === 'object' && !Array.isArray(cleaned[key])) {
        cleaned[key] = this.removeEmpty(cleaned[key]);
        if (Object.keys(cleaned[key]).length === 0) {
          delete cleaned[key];
        }
      }
    });
    return cleaned;
  }
}